import { useEffect, useState, useCallback, useMemo } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { RajasthanDiscomData } from "../../../models/rajasthan";
import {
  fetchRvpnData,
  getRajasthanDiscomData,
  getRajasthanForecastDiscomData,
} from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { useDispatch, useSelector } from "react-redux";
import { R_CHART_TYPES } from "../const";
import { GnaRuvnlFooter } from "../components";
import { DEFAULT_DATES, DataTabs, DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import { formatDateForAPI } from "../const";
import { FilterTab } from "../../../components/Filters";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";

import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL" },
  { label: "AVVNL", key: "AVVNL" },
];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const TYPE = ["Drawal", "Schedule", "Load Shedding", "Total Load"];
const UNITS = DEMAND_UNITS;

export function RajasthanTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedForecastType, setSelectedForecastType] = useState<any[]>([
    "Forecast D+1",
  ]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const uniqueDate = useMemo(
    () => DefaultDates[activeTab]?.trendDateOptions || [],
    [DefaultDates, activeTab]
  );
  const dispatch = useDispatch();

  const chartSuffixes = useMemo(
    () =>
      selectedGroupKeys === "RVPN Drawal"
        ? [
            {
              suffix: "drawal",
              displayName: "RVPN Drawal",
              stackSuffix: "rvpnl_drawal",
            },
            {
              suffix: "deviation",
              displayName: "Deviation %",
              stackSuffix: "deviation",
            },
          ]
        : [
            {
              suffix: "schedule",
              displayName: "Schedule",
              stackSuffix: "schedule",
            },
            { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
            {
              suffix: "load_shedding",
              displayName: "Load Shedding",
              stackSuffix: "load_shedding",
            },
            {
              suffix: "total",
              displayName: "Total Load",
              stackSuffix: "total",
            },
          ],
    [selectedGroupKeys]
  );

  const [selectedSuffix, setSelectedSuffix] = useState(
    chartSuffixes.slice(0, 2)
  );

  useEffect(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      setSelectedSuffix(chartSuffixes);
    } else {
      setSelectedSuffix(chartSuffixes.slice(0, 2));
    }
  }, [chartSuffixes, selectedGroupKeys]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    // Replace T+1 and T+2 with D+1 and D+2 in the legend name
    const displayKey = key
      .replace("_", " ")
      .replace("undefined", "")
      .replace(/T\+1/g, "D+1")
      .replace(/T\+2/g, "D+2");
    
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${displayKey} ${displayName}`,
      yAxisId: suffix === "deviation" ? "right" : undefined,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index], 25)
          : suffix === "drawal"
          ? ExchangeColors[index]
          : suffix === "deviation"
          ? "#ff6b6b"
          : undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;
      let processedDates = [...originalDates];

      const modelsToAdd = [];
      for (const date of originalDates) {
        const { label, value } = date;
        if (label.replace(" to ", ",") === value) {
          modelsToAdd.push({
            forecast: true,
            isFixed: false,
            label: label + " (Forecast)",
            value: value + ",Model 1",
            model: "Model 1",
          });
        }
      }
      processedDates = [...processedDates, ...modelsToAdd];

      const promises = processedDates.map(async (date: any) => {
        try {
          const [startDate, endDate, model] = date.value.split(",");

          if (model) {
            if (selectedGroupKeys !== "RVPN Drawal") {
              return [];
            }
            const forecastedData = await getRajasthanForecastDiscomData(
              startDate,
              endDate,
              model
            );
            return forecastedData.map((item: any) => ({
              ...item,
              dates: date.label.replace(")", " ") + item.forcast_type + ")",
            }));
          } else {
            return selectedGroupKeys === "RVPN Drawal"
              ? (await fetchRvpnData(startDate, endDate)).map((item: any) => ({
                  ...item,
                  dates: date.label,
                  drawal_mwh: item.drawal_mw / 4,
                  drawal_mw: item.drawal_mw,
                  drawal_lu: item.drawal_mw / 400,
                  block: item.slot_id,
                  discom: "total",
                  total_mwh: (item.load_shedding_mw + item.drawal_mw) / 4,
                  total_mw: item.load_shedding_mw + item.drawal_mw,
                }))
              : (await getRajasthanDiscomData(startDate, endDate)).map(
                  (item) => ({
                    ...item,
                    dates: date.label,
                  })
                );
          }
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();

      if (newData.length === 0) {
        console.warn("No data returned from API calls");
      }

      let transformedBewData = newData.map((item) => {
        let monthValue = "Unknown";
        if (item.date_of_schedule) {
          const date = new Date(item.date_of_schedule);
          if (!isNaN(date.getTime())) {
            monthValue = date.toLocaleDateString("en-US", { month: "short" });
          }
        } else if (item.dates) {
          try {
            const dateMatch = item.dates.match(
              /(\d{4}-\d{2}-\d{2})|([A-Z]{3}-\d{2})/i
            );
            if (dateMatch) {
              const dateStr = dateMatch[0];
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                monthValue = date.toLocaleDateString("en-US", {
                  month: "short",
                });
              }
            }
          } catch (e) {
            console.error("Error parsing date from dates field:", e);
          }
        }

        return {
          ...item,
          total_mw: (item.load_shedding_mw || 0) + (item.drawal_mw || 0),
          month: monthValue,
        };
      });

      console.log(
        "🚀 ~ Transformed data with month:",
        transformedBewData.slice(0, 5)
      );
      setData([...transformedBewData]);
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGroupKeys]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);

    if (GROUP_BY[index] === "RVPN Drawal") {
      setSelectedForecastType(["Forecast D+1"]);
    }
  }

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedXaxis.key === "time") {
        return ["dateIndex", "block"];
      } else if (selectedXaxis.key === "date") {
        return ["dateIndex"];
      } else if (selectedXaxis.key === "month") {
        return ["month"];
      }
    } else {
      return selectedXaxis.values;
    }
  }, [selectedBlock, selectedXaxis]);

  useEffect(() => {
    let filteredData = data;
    console.log(
      "🚀 ~ useEffect ~ before filtering filteredData:",
      filteredData
    );
    if (selectedGroupKeys !== "RVPN Drawal") {
      if (selectedDiscom.length > 0) {
        filteredData = data.filter((item) =>
          selectedDiscom.includes(item.discom)
        );
      }
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block)
      );
    }

    const aggregationKeys = [
      "schedule",
      "drawal",
      "load_shedding",
      "total",
    ].map((item) => item + "_" + selectedUnit.toLowerCase());

    const averageKeys = [
      "schedule_mw",
      "drawal_mw",
      "load_shedding_mw",
      "total_mw",
    ];

    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex", "block", "month"],
      groupBy:
        selectedGroupKeys === "RVPN Drawal"
          ? ["dates"]
          : ["dates"].concat(
              selectedGroupKeys === "Discom Load" ? ["discom"] : []
            ),
      volumeKey: ``,
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["block", "dateIndex"],
      priceKey: "",
      data: filteredData,
    };
    console.log("🚀 ~ useEffect ~ filteredData:", filteredData);
    const cData = FormatChartData(par);
    console.log("🚀 ~ useEffect ~ cData:", cData);

    if (selectedGroupKeys === "RVPN Drawal") {
      const processedData = cData.data.map((dataPoint) => {
        const newDataPoint = { ...dataPoint };

        cData.keys.forEach((key) => {
          if (key.includes("Forecast")) {
            const actualDateKey = key
              .replace(" (Forecast T+1)", "")
              .replace(" (Forecast T+2)", "");
            const actualKeyForForecast = `${actualDateKey}_drawal_${selectedUnit.toLowerCase()}`;
            const forecastKey = `${key}_drawal_${selectedUnit.toLowerCase()}`;

            const actual = dataPoint[actualKeyForForecast] ?? 0;
            const forecast = dataPoint[forecastKey] ?? 0;

            // Calculate deviation % = |Actual - Forecasted| / Actual * 100
            // Show 0 deviation when either actual is 0 or forecast is 0
            let deviation = 0;
            if (actual !== 0 && forecast !== 0) {
              deviation = (Math.abs(actual - forecast) / actual) * 100;
            }
            
            const deviationKey = `${key}_deviation_${selectedUnit.toLowerCase()}`;
            newDataPoint[deviationKey] = deviation;
          }
        });

        return newDataPoint;
      });

      setChartData(processedData);
    } else {
      setChartData(cData.data);
    }

    console.log("🚀 ~ FilteredData:", filteredData);
    console.log("🚀 ~ cData:", cData);
    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedUnit,
    selectedDiscom,
    selectedArea,
    selectedBlock,
    selectedSuffix,
    handleKey,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(unitParam: string) {
    setSelectedUnit(unitParam);
  }

  function getTypes() {
    if (selectedGroupKeys === "RVPN Drawal") {
      return ["Forecast D+1", "Forecast D+2"];
    } else {
      return ["Drawal", "Schedule", "Load Shedding", "Total Load"];
    }
  }

  function getLegends() {
    const legends = selectedSuffix
      .flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key: any, index: number) => {
          if (suffix === "deviation" && !key.includes("Forecast")) {
            return null;
          }

          return createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedXaxis,
            selectedChartType as any,
            ExchangeColors
          );
        })
      )
      .filter((item: any) => item !== null)
      .filter(
        (item: any) =>
          !(item.name?.includes("Forecast") && item.name.includes("Schedule"))
      )
      .filter(
        (item: any) =>
          !(
            item.name?.includes("Forecast") &&
            item.name.includes("Load Shedding")
          )
      )
      .filter((item: any) => {
        if (
          !item.name?.includes("Forecast") &&
          !item.name?.includes("Deviation %")
        ) {
          return true;
        }

        if (item.name?.includes("Deviation %")) {
          if (selectedForecastType.length === 0) {
            return false;
          }
          return selectedForecastType.some((type) => {
            const forecastNum = type.replace("Forecast ", "");
            // Check for both D+ (display) and T+ (data) formats
            return item.name?.includes(forecastNum) || item.name?.includes(forecastNum.replace("D+", "T+"));
          });
        }

        if (item.name?.includes("Forecast")) {
          return selectedForecastType.some((type) => {
            const forecastNum = type.replace("Forecast ", "");
            // Check for both D+ (display) and T+ (data) formats
            return item.name?.includes(forecastNum) || item.name?.includes(forecastNum.replace("D+", "T+"));
          });
        }

        return true;
      });
    return legends;
  }

  function summaryExtraKeys() {
    if (selectedXaxis.isTime) {
      return ["block", "dateIndex"];
    } else if (selectedXaxis.key === "date") {
      return ["dateIndex"];
    } else {
      return [selectedXaxis.values[0]];
    }
  }

  const summaryExtraLabels = selectedXaxis.values.map((value) =>
    value === "date" ? "Date" : value
  );

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedXaxis.values[0];
    }
  }, [selectedXaxis.key, selectedXaxis.values, selectedBlock.length]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);
  const commonSummaryProps = {
    legends: getLegends() as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load Trend Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Discom Load"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={(item) => {
              if (selectedGroupKeys === "RVPN Drawal") {
                dispatch(setLoadingFilterName(""));
                return;
              }
              setSelectedDiscom(item);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />
          <FilterMenu
            title="Type"
            items={getTypes()}
            selectedItems={
              selectedGroupKeys === "RVPN Drawal"
                ? selectedForecastType
                : selectedSuffix.map((item) => item.displayName)
            }
            setSelectedItems={(selected: string[]) => {
              if (selectedGroupKeys === "RVPN Drawal") {
                if (selected.length === 0 || selected.length > 2) {
                  setSelectedForecastType(["Forecast D+1", "Forecast D+2"]);
                  return;
                }
                setSelectedForecastType(selected);
                dispatch(setLoadingFilterName(""));
                return;
              }
              setSelectedSuffix(
                chartSuffixes.filter((suffixObj) =>
                  selected.includes(suffixObj.displayName)
                )
              );
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              {" "}
              {selectedGroupKeys} ({selectedUnit}){" "}
            </h2>
            <div className="flex gap-x-6 w-full justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(commonSummaryProps);
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(commonSummaryProps);
                  } else if (dataTab.label === "Table") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedXaxis.isTime}
              onlyTitle={true}
              secondYAxisDomain={[0, 100]}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              secondYAxisLabel="Deviation %"
              legends={getLegends() as any}
              xDataKey={xDataKey}
              secondXDataKey={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? selectedXaxis.values[1]
                  : undefined
              }
              xTick={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...commonSummaryProps} />
          )}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  DISCOM Energy Reports (RUVITL), Load Shedding Reports (RUVITL)
                </p>
              }
              text="1.   Actual Drawal = Restricted Demand
              Total Load (Unrestricted Demand) = Actual Drawal + Load Shedding 
2.   Load Shedding has been distributed across time blocks based on the actual duration of load shedding
3.  MW = Represents the average (MW) when the selected time interval is Date or Month.
4. RVPN Drawal = Total Drawal of Rajasthan.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
